import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { users as usersApi, stores as storesApi, smsOptins as smsOptinsApi, formatApiError } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, ShieldCheck, Phone as PhoneIcon, Bell, MessageSquare, Link2, X, Lock, LockOpen, KeyRound, CheckSquare, Download, Copy, Users as UsersIcon } from "lucide-react";

const ROLES = ["admin", "auditor", "gerente", "direccion"];
const PREFS = [
  { key: "sms_multa", i18n: "users.notif_multa" },
  { key: "sms_to_do", i18n: "users.notif_to_do" },
  { key: "sms_no_followup_24h", i18n: "users.notif_no_followup_24h" },
  { key: "sms_reports", i18n: "users.notif_reports" },
  { key: "sms_alerts", i18n: "users.notif_alerts" },
  { key: "sms_todo_completed", i18n: "users.notif_todo_completed" },
  { key: "sms_assignment", i18n: "users.notif_assignment" },
  { key: "sms_task_update", i18n: "users.notif_task_update" },
  { key: "sms_overdue", i18n: "users.notif_overdue" },
];

const EMPTY_PREFS = PREFS.reduce((acc, p) => ({ ...acc, [p.key]: false }), {});

export default function UsersPage() {
  const { t } = useTranslation();
  const { user: me } = useApp();
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [tempResults, setTempResults] = useState(null); // { results: [...] } from bulk reset
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    role: "auditor",
    assigned_store_id: "",
    phone: "",
    notification_prefs: { ...EMPTY_PREFS },
  });
  const [busy, setBusy] = useState(false);
  const [optins, setOptins] = useState([]);
  const [linkSel, setLinkSel] = useState({});

  const load = async () => {
    const [u, s, o] = await Promise.all([usersApi.list(), storesApi.list(true), smsOptinsApi.list()]);
    setItems(u);
    setStores(s);
    setOptins(o);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing(null);
    setForm({
      email: "",
      password: "",
      name: "",
      role: "auditor",
      assigned_store_id: "",
      phone: "",
      notification_prefs: { ...EMPTY_PREFS },
    });
    setOpen(true);
  };

  const startEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email,
      password: "",
      name: u.name,
      role: u.role,
      assigned_store_id: u.assigned_store_id || "",
      active: u.active,
      phone: u.phone || "",
      notification_prefs: { ...EMPTY_PREFS, ...(u.notification_prefs || {}) },
    });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        await usersApi.patch(editing.id, {
          name: form.name,
          role: form.role,
          assigned_store_id: form.assigned_store_id || null,
          active: form.active,
          password: form.password || undefined,
          phone: form.phone.trim(),
          notification_prefs: form.notification_prefs,
        });
        toast.success(t("common.save"));
      } else {
        await usersApi.create({
          email: form.email,
          password: form.password,
          name: form.name,
          role: form.role,
          assigned_store_id: form.assigned_store_id || null,
          phone: form.phone.trim() || undefined,
          notification_prefs: form.notification_prefs,
        });
        toast.success(t("users.new_user"));
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(t("users.delete_confirm", { name: u.name }))) return;
    try {
      await usersApi.remove(u.id);
      toast.success(t("common.delete"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const unlockUser = async (u) => {
    if (!window.confirm(t("users.unlock_confirm", { name: u.name }))) return;
    try {
      await usersApi.unlock(u.id);
      toast.success(t("users.unlocked", { name: u.name }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(t("users.reset_password_prompt", { name: u.name }), "");
    if (pw === null) return;
    if (!pw || pw.trim().length < 6) {
      toast.error(t("users.password_too_short"));
      return;
    }
    try {
      await usersApi.patch(u.id, { password: pw.trim() });
      try { await usersApi.unlock(u.id); } catch { /* ignore */ }
      toast.success(t("users.password_reset", { name: u.name }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // ===== Bulk selection =====
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const isSelectedAll = items.length > 0 && items.every((u) => selectedIds.has(u.id));
  const toggleSelectAll = () => {
    if (isSelectedAll) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((u) => u.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkUnlock = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(t("users.bulk_unlock_confirm", { count: ids.length }))) return;
    try {
      const r = await usersApi.bulkUnlock(ids);
      toast.success(t("users.bulk_unlocked", { count: r.unlocked }));
      clearSelection();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const bulkResetPassword = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(t("users.bulk_reset_confirm", { count: ids.length }))) return;
    try {
      const r = await usersApi.bulkResetPassword(ids);
      setTempResults(r);
      clearSelection();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadTempsCsv = () => {
    if (!tempResults?.results?.length) return;
    const rows = [["Name", "Email", "Temporary password"], ...tempResults.results.map((r) => [r.name || "", r.email, r.temp_password])];
    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `canales-temp-passwords-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const storeName = (id) => stores.find((s) => s.id === id)?.tienda || "—";

  const togglePref = (key, checked) => {
    setForm((f) => ({
      ...f,
      notification_prefs: { ...f.notification_prefs, [key]: !!checked },
    }));
  };

  const activePrefCount = (u) => {
    const p = u.notification_prefs || {};
    return PREFS.filter((x) => p[x.key]).length;
  };

  const pendingOptins = optins.filter((o) => o.status === "pending");

  const linkOptin = async (o) => {
    const uid = linkSel[o.id];
    if (!uid) {
      toast.error(t("users.optin_select_user"));
      return;
    }
    try {
      await smsOptinsApi.link(o.id, uid);
      toast.success(t("users.optin_linked_toast"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const dismissOptin = async (o) => {
    try {
      await smsOptinsApi.dismiss(o.id);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="users-page">
      <section className="hero-night text-white">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-12">
          <div className="canales-overline text-stone-300 flex items-center gap-2">
            <ShieldCheck size={12} /> {t("nav.users")}
          </div>
          <h1 className="font-display text-4xl md:text-6xl mt-2 leading-[1.04]">{t("users.title")}</h1>
          <p className="text-stone-300 mt-3 max-w-2xl text-sm md:text-base">{t("users.subtitle")}</p>
        </div>
      </section>

      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-8">
        <div className="flex justify-end mb-4">
          <Button onClick={startNew} className="brand-button gap-2" data-testid="new-user-button">
            <Plus size={14} /> {t("users.new_user")}
          </Button>
        </div>

        {pendingOptins.length > 0 && (
          <Card className="canales-card mb-5 p-5 border-l-4 border-amber-400" data-testid="optin-requests-card">
            <div className="canales-overline flex items-center gap-1.5">
              <MessageSquare size={12} /> {t("users.optin_title")}
              <span className="pill bg-amber-50 text-amber-700">{pendingOptins.length}</span>
            </div>
            <p className="text-[11px] text-stone-500 mt-0.5 mb-3">{t("users.optin_hint")}</p>
            <div className="space-y-2">
              {pendingOptins.map((o) => (
                <div
                  key={o.id}
                  className="flex flex-wrap items-center gap-3 rounded-lg border border-stone-200 p-3"
                  data-testid={`optin-row-${o.phone}`}
                >
                  <div className="min-w-[180px]">
                    <div className="font-medium text-sm">{o.name}</div>
                    <div className="text-xs text-stone-500 tabular-nums">
                      {o.phone}
                      {o.store ? ` · ${o.store}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-auto flex-wrap">
                    <Select
                      value={linkSel[o.id] || ""}
                      onValueChange={(v) => setLinkSel((s) => ({ ...s, [o.id]: v }))}
                    >
                      <SelectTrigger className="w-[200px] h-9" data-testid={`optin-user-select-${o.phone}`}>
                        <SelectValue placeholder={t("users.optin_select_user")} />
                      </SelectTrigger>
                      <SelectContent>
                        {items.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      className="brand-button gap-1.5"
                      onClick={() => linkOptin(o)}
                      data-testid={`optin-link-${o.phone}`}
                    >
                      <Link2 size={13} /> {t("users.optin_link")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => dismissOptin(o)}
                      data-testid={`optin-dismiss-${o.phone}`}
                    >
                      <X size={13} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card className="canales-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <input
                    type="checkbox"
                    checked={isSelectedAll}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-stone-300 cursor-pointer"
                    aria-label={t("users.select_all", "Seleccionar todo")}
                    data-testid="users-select-all"
                  />
                </TableHead>
                <TableHead>{t("common.name")}</TableHead>
                <TableHead>{t("common.email")}</TableHead>
                <TableHead>{t("users.phone_col")}</TableHead>
                <TableHead>{t("common.role")}</TableHead>
                <TableHead>{t("common.assigned_store")}</TableHead>
                <TableHead className="text-center">
                  <Bell size={14} className="inline-block" />
                </TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((u) => (
                <TableRow
                  key={u.id}
                  data-testid={`user-row-${u.email}`}
                  className={selectedIds.has(u.id) ? "bg-amber-50/40" : ""}
                >
                  <TableCell className="w-8">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(u.id)}
                      onChange={() => toggleSelected(u.id)}
                      className="h-4 w-4 rounded border-stone-300 cursor-pointer"
                      aria-label={t("users.select_row", { name: u.name })}
                      data-testid={`select-user-${u.email}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-stone-600">{u.email}</TableCell>
                  <TableCell className="text-stone-600 text-xs tabular-nums">
                    {u.phone || <span className="text-stone-400">—</span>}
                  </TableCell>
                  <TableCell>
                    <span className="pill">{t(`roles.${u.role}`)}</span>
                  </TableCell>
                  <TableCell className="text-stone-600">{storeName(u.assigned_store_id)}</TableCell>
                  <TableCell className="text-center text-xs text-stone-600">
                    {activePrefCount(u) > 0 ? (
                      <span className="pill bg-amber-50 text-amber-700">{activePrefCount(u)}/5</span>
                    ) : (
                      <span className="text-stone-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {u.locked_until ? (
                      <span
                        className="pill bg-red-50 text-red-700 inline-flex items-center gap-1"
                        title={t("users.locked_until_tooltip", { when: new Date(u.locked_until).toLocaleString() })}
                        data-testid={`user-locked-badge-${u.email}`}
                      >
                        <Lock size={11} />
                        {t("users.locked", "Bloqueado")}
                      </span>
                    ) : u.active ? (
                      <span className="pill bg-emerald-50 text-emerald-700">{t("common.active")}</span>
                    ) : (
                      <span className="pill bg-red-50 text-red-700">{t("common.inactive")}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => startEdit(u)} data-testid={`edit-user-${u.email}`}>
                        <Edit3 size={14} />
                      </Button>
                      {u.locked_until && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => unlockUser(u)}
                          className="text-amber-700 hover:text-amber-900 hover:bg-amber-50"
                          title={t("users.unlock", "Desbloquear")}
                          data-testid={`unlock-user-${u.email}`}
                        >
                          <LockOpen size={14} />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resetPassword(u)}
                        className="text-stone-600 hover:text-blue-700 hover:bg-blue-50"
                        title={t("users.reset_password", "Restablecer contraseña")}
                        data-testid={`reset-password-${u.email}`}
                      >
                        <KeyRound size={14} />
                      </Button>
                      {u.id !== me?.id && (
                        <Button size="sm" variant="outline" onClick={() => remove(u)} data-testid={`delete-user-${u.email}`}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* Floating bulk-action bar — appears only when 1+ users selected. */}
        {selectedIds.size > 0 && (
          <div
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 bg-[#0B1220] text-white rounded-full shadow-2xl px-4 py-2 flex items-center gap-2"
            data-testid="bulk-action-bar"
          >
            <UsersIcon size={14} className="text-amber-400" />
            <span className="text-xs font-semibold">
              {t("users.bulk_selected", { count: selectedIds.size })}
            </span>
            <span className="w-px h-4 bg-white/20 mx-1" />
            <button
              type="button"
              onClick={bulkUnlock}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-amber-400 hover:bg-amber-300 text-stone-900 font-medium"
              data-testid="bulk-unlock-btn"
            >
              <LockOpen size={12} />
              {t("users.bulk_unlock", "Desbloquear")}
            </button>
            <button
              type="button"
              onClick={bulkResetPassword}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white hover:bg-stone-100 text-stone-900 font-medium"
              data-testid="bulk-reset-btn"
            >
              <KeyRound size={12} />
              {t("users.bulk_reset", "Resetear contraseñas")}
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="ml-1 text-white/70 hover:text-white p-1"
              title={t("common.clear", "Limpiar")}
              data-testid="bulk-clear-btn"
            >
              <X size={12} />
            </button>
          </div>
        )}

        {/* Bulk-reset results dialog — shows temp passwords once. */}
        <Dialog open={!!tempResults} onOpenChange={(v) => { if (!v) setTempResults(null); }}>
          <DialogContent className="max-w-2xl" data-testid="bulk-reset-results-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound size={18} className="text-amber-600" />
                {t("users.bulk_reset_done_title", "Contraseñas temporales generadas")}
              </DialogTitle>
              <DialogDescription>
                {t(
                  "users.bulk_reset_done_desc",
                  "Estas contraseñas se muestran una sola vez. Cópialas o descárgalas ahora. Cada usuario tendrá que elegir su propia contraseña al iniciar sesión."
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.name")}</TableHead>
                    <TableHead>{t("common.email")}</TableHead>
                    <TableHead>{t("users.temp_password", "Contraseña temporal")}</TableHead>
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(tempResults?.results || []).map((r) => (
                    <TableRow key={r.user_id}>
                      <TableCell className="font-medium text-sm">{r.name || "—"}</TableCell>
                      <TableCell className="text-stone-600 text-xs">{r.email}</TableCell>
                      <TableCell className="font-mono text-sm text-stone-800">{r.temp_password}</TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard?.writeText(r.temp_password);
                            toast.success(t("common.copied", "Copiado"));
                          }}
                          className="text-stone-500 hover:text-blue-700"
                          title={t("common.copy", "Copiar")}
                          data-testid={`copy-temp-${r.email}`}
                        >
                          <Copy size={13} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTempResults(null)} data-testid="bulk-reset-close">
                {t("common.close", "Cerrar")}
              </Button>
              <Button className="brand-button gap-1.5" onClick={downloadTempsCsv} data-testid="bulk-reset-csv">
                <Download size={14} />
                {t("users.download_csv", "Descargar CSV")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? t("users.edit_user") : t("users.new_user")}</DialogTitle>
              <DialogDescription>
                {editing ? t("users.leave_password_blank") : ""}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="canales-overline">{t("common.email")}</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  disabled={!!editing}
                  className="mt-1"
                  data-testid="user-email-input"
                />
              </div>
              <div>
                <label className="canales-overline">{t("common.name")}</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1"
                  data-testid="user-name-input"
                />
              </div>
              <div>
                <label className="canales-overline flex items-center gap-1.5">
                  <PhoneIcon size={12} /> {t("users.phone_label")}
                </label>
                <Input
                  type="tel"
                  value={form.phone}
                  placeholder={t("users.phone_placeholder")}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1 tabular-nums"
                  data-testid="user-phone-input"
                />
                <p className="text-[11px] text-stone-500 mt-1">{t("users.phone_hint")}</p>
              </div>
              <div>
                <label className="canales-overline">
                  {editing ? t("common.new_password") : t("common.password")}
                </label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-1"
                  data-testid="user-password-input"
                  autoComplete="new-password"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="canales-overline">{t("common.role")}</label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-1" data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r}>{t(`roles.${r}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="canales-overline">{t("common.assigned_store")}</label>
                  <Select
                    value={form.assigned_store_id || "none"}
                    onValueChange={(v) => setForm({ ...form, assigned_store_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="user-store-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("users.no_assignment")}</SelectItem>
                      {stores.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.tienda}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="pt-2 border-t border-stone-200">
                <div className="canales-overline flex items-center gap-1.5">
                  <Bell size={12} /> {t("users.notifications_title")}
                </div>
                <p className="text-[11px] text-stone-500 mt-0.5 mb-2">{t("users.notifications_hint")}</p>
                <div className="space-y-1.5">
                  {PREFS.map((p) => (
                    <label
                      key={p.key}
                      className="flex items-center gap-2.5 text-sm cursor-pointer py-1 px-2 rounded-md hover:bg-stone-50"
                      data-testid={`pref-${p.key}-label`}
                    >
                      <Checkbox
                        checked={!!form.notification_prefs[p.key]}
                        onCheckedChange={(v) => togglePref(p.key, v)}
                        disabled={!form.phone.trim()}
                        data-testid={`pref-${p.key}-checkbox`}
                      />
                      <span className={!form.phone.trim() ? "text-stone-400" : ""}>
                        {t(`users.notif_${p.key.replace("sms_", "")}`)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {editing && (
                <div className="flex items-center gap-2 pt-1">
                  <Switch
                    checked={form.active}
                    onCheckedChange={(v) => setForm({ ...form, active: v })}
                    data-testid="user-active-switch"
                  />
                  <span className="text-sm">{form.active ? t("common.active") : t("common.inactive")}</span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={busy} className="brand-button" data-testid="save-user-button">
                {t("common.save")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
